import * as React from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import FabricCanvas from "./FabricCanvas";
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState, useCallback, useRef, useEffect } from "react";
import "react-image-crop/dist/ReactCrop.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getPanelListStatus,
  selectPanelList,
  selectedTextCardIndex,
  selectedPageCardIndex,
  createNewStatus,
  fetchPanelList,
  updateIndex,
  resetIndex,
  toggleCreateNew,
} from "../features/panels/panelsSlice";
import { useParams } from "react-router-dom";
import Loader from "./Loader";
import { panelPatchReview } from "../api/panelPatchReview";
import { currentSelectedLanguage } from "../features/selectedLanguage/languageSlice";
import { Card } from "./Card";
import useKeyboardShortcut from "use-keyboard-shortcut";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function CanvasPanels() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const [change, setChange] = useState(false);
  const [currIndex, setCurrIndex] = useState(null);
  const selectedTextIndex = useSelector(selectedTextCardIndex);
  const selectedPageIndex = useSelector(selectedPageCardIndex);
  const data = useSelector(selectPanelList);
  console.log("data", data);
  const lang = useSelector(currentSelectedLanguage);
  console.log("lang", lang);
  const status = useSelector(getPanelListStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [currTextIndex, setCurrTextIndex] = useState(0);
  const statusCreateNew = useSelector(createNewStatus);
  const [crop, setCrop] = useState({
    unit: "%",
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const cardsContainerRef = useRef(null);
  const cardsPositionRef = useRef(0);
  const [isExpanded, setIsExpanded] = useState(false);


  useEffect(() => {
    if (value !== null) {
      setCurrTextIndex(0);
    }
  }, [value]);

  useKeyboardShortcut(
    ["ArrowDown"],
    (shortcutKeys) => {
      if (selectedTextIndex == null && selectedPageIndex == null) {
        setCurrTextIndex(
          currTextIndex < data[value].comic_texts.length - 1
            ? currTextIndex + 1
            : currTextIndex
        );
      }
    },
    {
      overrideSystem: false,
      ignoreInputFields: false,
      repeatOnHold: false,
    }
  );

  useKeyboardShortcut(
    ["ArrowUp"],
    (shortcutKeys) => {
      if (selectedTextIndex == null && selectedPageIndex == null) {
        setCurrTextIndex(currTextIndex > 0 ? currTextIndex - 1 : 0);
      }
    },
    {
      overrideSystem: false,
      ignoreInputFields: false,
      repeatOnHold: false,
    }
  );

  useKeyboardShortcut(
    ["Enter"],
    (shortcutKeys) => {
      if (
        selectedTextIndex == null &&
        selectedPageIndex == null &&
        data[value].comic_texts.length > 0
      ) {
        handleBBoxClick(currTextIndex, value);
      }
    },
    {
      overrideSystem: true,
      ignoreInputFields: true,
      repeatOnHold: false,
    }
  );


  useEffect(() => {
    if (statusCreateNew && status === "loading") {
      setIsLoading(true);
    }
  }, [statusCreateNew, status]);

  useEffect(() => {
    if (statusCreateNew && status === "succeeded" && isLoading) {
      handleBBoxClick(data[value].comic_texts.length - 1, value);
      dispatch(toggleCreateNew());
      setIsLoading(false);
    }
  }, [statusCreateNew, status]);

  const handleChange = (event, newValue) => {
    dispatch(resetIndex());
    setValue(newValue);
  };

  const handleDone = async (state) => {
    if (state === false) {
      setValue(value + 1);
    }
    let id = data[value].id;
    let dt = {
      is_reviewed: !state,
    };
    await panelPatchReview(id, dt);
    setChange(true);
  };

  useEffect(() => {
    if (change) {
      dispatch(fetchPanelList(id));
      setChange(false);
    }
  }, [change, dispatch, id, lang]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollPositionRef.current;
    }
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollTop = cardsPositionRef.current;
    }
  }, [data]);

  const handleClick = (textIndex) => {
    setValue(textIndex);
    dispatch(resetIndex());
  };

  const handleBBoxClick = (textIndex, pageIndex) => {
    dispatch(updateIndex({ textIndex: textIndex, pageIndex: pageIndex }));
  };

  const renderCard = useCallback(
    (el, textIndex, pageIndex) => {
      return (
        <Card
          key={el.index}
          index={textIndex}
          pageIndex={pageIndex}
          fromExtraction={false}
          id={el.index}
          text={el.text_cleaned}
          isFocused={textIndex === currTextIndex}
        />
      );
    },
    [currTextIndex]
  );

  const handleFabricBoxClick = (boxIndex) => {
    setCurrTextIndex(boxIndex);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container sx={{ alignItems: "center", marginTop: "0.75rem" }}>
        <Grid item xs={8} md={8}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: ".1rem",
              },
            }}
          >
            {data &&
              data.map((panel, pageIndex) => (
                <Tooltip title={pageIndex + 1}>
                  <IconButton
                    onClick={() => handleClick(pageIndex)}
                    disabled={
                      selectedTextIndex != null && selectedPageIndex != null
                    }
                    size="small"
                    disableRipple
                  >
                    {panel.is_reviewed ? (
                      <CheckCircleIcon
                        color={value !== pageIndex ? "success" : "primary"}
                        fontSize="large"
                      />
                    ) : (
                      <Paper
                        elevation={3}
                        sx={{
                          borderRadius: "50%",
                          aspectRatio: 1,
                          width: "1.6rem",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor:
                            value === pageIndex ? "primary.main" : undefined,
                          color:
                            value === pageIndex
                              ? "primary.contrastText"
                              : undefined,
                        }}
                      >
                        {pageIndex + 1}
                      </Paper>
                    )}
                  </IconButton>
                </Tooltip>
              ))}
          </Stack>
        </Grid>
        <Grid item xs={4} md={4}>
          <div
            style={{
              display: "flex",
              justifyContent: "right",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                if (data[value]) {
                  handleDone(data[value].is_reviewed);
                }
              }}
              disabled={selectedTextIndex != null && selectedPageIndex != null}
            >
              {data[value] && data[value].is_reviewed ? (
                <>Mark as Undone</>
              ) : (
                <>Mark as done</>
              )}
            </Button>
          </div>
        </Grid>
      </Grid>
      <Box display="flex" sx={{ width: "100%", maxHeight: "90%" }}>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={value}
          onChange={handleChange}
          sx={{ height: "80vh" }}
        >
        </Tabs>
        <Box style={{ width: "100%" }}>
          {data &&
            data.map((panel, pageIndex) => {
              return (
                console.log("panel value", panel.panel_composite_image),
                console.log("panel value name", panel.panel_composite_image.name),
                <TabPanel value={value} index={pageIndex}>
                  {status === "succeeded" ? (
                    <Grid
                      container
                      spacing={2}
                      sx={{ height: "80vh", m: 0, p: 0 }}
                    >
                      <Grid item xs={isExpanded ? 9 : 6} sx={{ height: "100%" }}>
                        <FabricCanvas
                          panel_num = {panel.panel_num}
                          comic_file_id={panel.input_file}
                          initialColor="#35363a"
                          initialCanvasWidth={panel.meta.panel_dims[0]}
                          initialCanvasHeight={panel.meta.panel_dims[1]}
                          backgroundImageUrl={panel.panel_composite_image}
                          textBoxes={panel.comic_texts.map((item) => item.bounding_box)}
                          handleBBoxClick={handleFabricBoxClick}
                          selectedBoxIndex={currTextIndex}
                        />
                      </Grid>
                      <Grid item xs={isExpanded ? 3 : 6} sx={{ height: "100%" }}>
                        <Stack direction="row-reverse" gap={1} mb="0.5rem"></Stack>
                        <Stack
                          style={{ height: "95%", overflowY: "auto" }}
                          spacing={2}
                          ref={cardsContainerRef}
                        >
                          {panel.comic_texts.map((el, textIndex) => {
                            const translation = el.translations.find(
                              (t) => t.language === lang
                            );

                            if (translation) {
                              const translatedEl = {
                                ...el,
                                text_cleaned:
                                  translation.translation_final?.text ||
                                  "No final translation",
                                original_text: el.text_cleaned,
                                translation_id: translation.id,
                              };

                              return renderCard(translatedEl, textIndex, pageIndex);
                            } else {
                              return renderCard(el, textIndex, pageIndex);
                            }
                          })}
                        </Stack>
                      </Grid>
                    </Grid>
                  ) : (
                    <Loader />
                  )}
                </TabPanel>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
}
